import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, Zap, TrendingUp, ArrowRight, Clock, Cpu, DollarSign, BarChart3 } from 'lucide-react';
import { useLanguage } from '../lib/i18n';

const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6 } }) };

const philosophyItems = [
  {
    icon: Clock,
    title: { en: 'Speed', ar: 'السرعة' },
    desc: { en: "Why wait weeks for a shoot when you can have high-converting ads in days?", ar: "لماذا تنتظر أسابيع لتصوير عندما يمكنك الحصول على إعلانات عالية التحويل في أيام؟" },
    color: '#06B6D4',
  },
  {
    icon: DollarSign,
    title: { en: 'Efficiency', ar: 'الكفاءة' },
    desc: { en: "We cut the fluff and the high production fees, passing the savings — and the results — directly to you.", ar: "نحن نتخلص من الحشو والرسوم الإنتاجية العالية، ونمرر المدخرات — والنتائج — إليك مباشرة." },
    color: '#7C3AED',
  },
  {
    icon: BarChart3,
    title: { en: 'Impact', ar: 'التأثير' },
    desc: { en: "We don't care about \"likes.\" We care about your bottom line.", ar: "لا نهتم بـ \"الإعجابات\". نهتم بأرباحك." },
    color: '#A855F7',
  },
];

export default function About() {
  const { lang, isRTL } = useLanguage();

  const content = {
    en: {
      tag: 'About ZS Digitals',
      title: 'Stop Counting Leads.',
      titleHighlight: 'Start Counting Sales.',
      intro: "Most agencies hide behind \"vanity metrics.\" They'll show you a dashboard full of impressions, clicks, and a long list of phone numbers, then walk away while your sales remain at zero.",
      different: "We're different because we've seen the frustration firsthand. We founded our agency to bridge the gap between a \"lead\" and a \"customer.\" We realized that businesses don't need more data to look at — they need more revenue to grow. We don't just hand you a spreadsheet; we provide the creative engine that turns attention into transactions.",
      aiTag: 'Our Edge',
      aiTitle: 'High-Performance Content at the Speed of AI',
      aiDesc: "The biggest barrier to scaling is often the cost and time of content production. We've removed that barrier.",
      aiDetail: "By leveraging cutting-edge AI Video Technology, we produce hyper-realistic UGC (User Generated Content) ads at a fraction of the traditional cost and in a fraction of the time. Whether you are in furniture, medical, apparel, or tech, our AI-driven pipeline allows us to test, iterate, and scale winning creatives across any niche instantly.",
      serveTag: 'Who We Serve',
      serveTitle: 'Built for Ambitious Brands',
      serveDesc: "We partner with SMEs (Small and Medium Enterprises) who are ready to push their brands to new heights. We work with ambitious founders who are tired of the old agency model and are ready to embrace AI-powered marketing to dominate their market.",
      philTag: 'Our Philosophy',
      philTitle: 'Simple. Direct. Results.',
      ctaTitle: 'Ready to see what your brand can do with the right technology behind it?',
      ctaBtn: 'Get Started',
    },
    ar: {
      tag: 'عن زد ديجيتالز',
      title: 'توقف عن عد العملاء المحتملين.',
      titleHighlight: 'ابدأ بعد المبيعات.',
      intro: "معظم الوكالات تختبئ وراء \"مقاييس الغرور\". سيعرضون لك لوحة مليئة بالانطباعات والنقرات وقائمة طويلة من أرقام الهواتف، ثم يغادرون بينما مبيعاتك تبقى صفراً.",
      different: "نحن مختلفون لأننا رأينا الإحباط بأنفسنا. أسسنا وكالتنا لسد الفجوة بين \"العميل المحتمل\" و\"العميل الفعلي\". أدركنا أن الشركات لا تحتاج إلى المزيد من البيانات — بل تحتاج إلى المزيد من الإيرادات. نحن لا نسلمك جدول بيانات فحسب؛ بل نوفر محرك الإبداع الذي يحول الانتباه إلى مبيعات.",
      aiTag: 'ميزتنا',
      aiTitle: 'محتوى عالي الأداء بسرعة الذكاء الاصطناعي',
      aiDesc: "أكبر عائق أمام التوسع هو غالباً تكلفة ووقت إنتاج المحتوى. لقد أزلنا هذا العائق.",
      aiDetail: "من خلال الاستفادة من تقنية فيديو الذكاء الاصطناعي المتطورة، ننتج إعلانات UGC واقعية للغاية بجزء من التكلفة التقليدية وفي جزء من الوقت. سواء كنت في الأثاث أو الطب أو الملابس أو التقنية، خط إنتاجنا بالذكاء الاصطناعي يسمح لنا بالاختبار والتكرار وتوسيع الإبداعات الفائزة عبر أي مجال فوراً.",
      serveTag: 'من نخدم',
      serveTitle: 'مبنية للعلامات التجارية الطموحة',
      serveDesc: "نتشارك مع الشركات الصغيرة والمتوسطة المستعدة لدفع علاماتها التجارية إلى آفاق جديدة. نعمل مع المؤسسين الطموحين الذين سئموا من نموذج الوكالة القديم ومستعدون لتبني التسويق بالذكاء الاصطناعي للهيمنة على سوقهم.",
      philTag: 'فلسفتنا',
      philTitle: 'بسيطة. مباشرة. نتائج.',
      ctaTitle: 'مستعد لترى ما يمكن أن تفعله علامتك التجارية مع التقنية المناسبة خلفها؟',
      ctaBtn: 'ابدأ الآن',
    },
  };

  const c = content[lang];

  return (
    <div data-testid="about-page" className="bg-[#050508]">
      {/* Hero */}
      <section className="pt-28 sm:pt-32 pb-16 sm:pb-20 md:pt-40 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(124,58,237,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(124,58,237,.5) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-[radial-gradient(ellipse_at_center,rgba(124,58,237,0.12)_0%,transparent_70%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.span variants={fadeUp} initial="hidden" animate="visible" custom={0} className="text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4] mb-4 block">{c.tag}</motion.span>
          <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={1} className="font-poppins font-black text-3xl sm:text-5xl lg:text-7xl text-white leading-[1.1] tracking-tighter max-w-4xl">
            {c.title} <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7C3AED] via-[#A855F7] to-[#06B6D4]">{c.titleHighlight}</span>
          </motion.h1>
        </div>
      </section>

      {/* Main Story */}
      <section className="py-16 sm:py-24 md:py-32 bg-[#0A0A12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-6">
            <p className="text-[#A1A1AA] text-base sm:text-lg leading-relaxed">{c.intro}</p>
            <p className="text-white/90 text-base sm:text-lg leading-relaxed font-medium">{c.different}</p>
          </motion.div>
        </div>
      </section>

      {/* AI Edge */}
      <section className="py-16 sm:py-24 md:py-32 bg-[#050508] relative">
        <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.03, 0.07, 0.03] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }} className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-[#7C3AED] blur-[150px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: isRTL ? 40 : -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4] mb-4 block">{c.aiTag}</span>
              <h2 className="font-poppins font-bold text-2xl sm:text-3xl md:text-4xl text-white tracking-tight mb-4 sm:mb-6">{c.aiTitle}</h2>
              <p className="text-[#A1A1AA] text-sm sm:text-base leading-relaxed mb-4">{c.aiDesc}</p>
              <p className="text-[#A1A1AA] text-sm sm:text-base leading-relaxed">{c.aiDetail}</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: isRTL ? -40 : 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {[
                  { icon: Cpu, label: { en: 'AI Video Tech', ar: 'تقنية فيديو ذكاء اصطناعي' }, value: { en: 'Hyper-Realistic', ar: 'واقعية فائقة' } },
                  { icon: Clock, label: { en: 'Production Time', ar: 'وقت الإنتاج' }, value: { en: 'Days, Not Weeks', ar: 'أيام، ليس أسابيع' } },
                  { icon: DollarSign, label: { en: 'Cost', ar: 'التكلفة' }, value: { en: 'Fraction of Traditional', ar: 'جزء من التقليدي' } },
                  { icon: TrendingUp, label: { en: 'Scale', ar: 'التوسع' }, value: { en: 'Any Niche, Instantly', ar: 'أي مجال، فوراً' } },
                ].map((item) => (
                  <div key={item.label.en} className="p-4 sm:p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-[#7C3AED]/20 transition-all">
                    <item.icon className="w-5 h-5 text-[#7C3AED] mb-2" />
                    <div className="text-white font-poppins font-semibold text-sm sm:text-base mb-0.5">{item.value[lang]}</div>
                    <div className="text-[#71717A] text-xs">{item.label[lang]}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-16 sm:py-24 md:py-32 bg-[#0A0A12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4] mb-4 block">{c.serveTag}</span>
            <h2 className="font-poppins font-bold text-2xl sm:text-3xl md:text-4xl text-white tracking-tight mb-4 sm:mb-6">{c.serveTitle}</h2>
            <p className="text-[#A1A1AA] text-sm sm:text-lg leading-relaxed max-w-2xl mx-auto">{c.serveDesc}</p>
          </motion.div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-16 sm:py-24 md:py-32 bg-[#050508]" data-testid="philosophy-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 sm:mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4] mb-4 block">{c.philTag}</span>
            <h2 className="font-poppins font-bold text-2xl sm:text-3xl md:text-4xl text-white tracking-tight">{c.philTitle}</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {philosophyItems.map((item, i) => (
              <motion.div
                key={item.title.en}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                whileHover={{ y: -6, borderColor: `${item.color}40` }}
                className="p-6 sm:p-8 rounded-2xl bg-white/[0.02] border border-white/[0.06] transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${item.color}15` }}>
                  <item.icon className="w-5 h-5" style={{ color: item.color }} />
                </div>
                <h3 className="font-poppins font-bold text-lg sm:text-xl text-white mb-2">{item.title[lang]}</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{item.desc[lang]}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 md:py-32 bg-[#0A0A12] relative overflow-hidden">
        <motion.div animate={{ scale: [1, 1.3, 1], opacity: [0.04, 0.1, 0.04] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#7C3AED] blur-[150px]" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="font-poppins font-bold text-xl sm:text-2xl md:text-3xl text-white tracking-tight mb-6 sm:mb-8">
            {c.ctaTitle}
          </motion.h2>
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 sm:px-10 py-3 sm:py-4 bg-[#7C3AED] text-white font-semibold rounded-full hover:bg-[#6D28D9] hover:shadow-[0_8px_30px_rgba(124,58,237,0.4)] transition-all duration-300 text-base sm:text-lg">
            {c.ctaBtn} <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
